import { useSelector } from 'react-redux';

const permissions = () => {
	const { userInfo } = useSelector((state) => state.auth);

	const getPermissions = (category, subcategory, permissionName) => {
		const subcategoryPermissions
			= userInfo?.menu?.[category]?.[subcategory] || [];
		if (permissionName) {
			return subcategoryPermissions.some(
				(item) => item.permissionName === permissionName
			);
		}

		return subcategoryPermissions.length > 0;
	};

	/* History Permissions */
	const HistoryWorkflow = getPermissions(
		'History',
		'history',
		'history_workflow'
	);
	const HistoryWalletTransaction = getPermissions(
		'History',
		'history',
		'history_wallet_transaction'
	);
	const AccountLedger = getPermissions(
		'History',
		'history',
		'history_account_ledger'
	);
	const SmsHistory = getPermissions('History', 'sms_history', 'sms_history');
	const EmailHistory = getPermissions(
		'History',
		'email_history',
		'email_history'
	);

	/* Template Permissions */
	const TemplateEngineView = getPermissions(
		'Template',
		'template_engine',
		'template_engine_view'
	);
	const TemplateEngineEdit = getPermissions(
		'Template',
		'template_engine',
		'template_engine_edit'
	);
	const TemplateEngineAdd = getPermissions(
		'Template',
		'template_engine',
		'template_engine_add'
	);
	const TemplateEngineDelete = getPermissions(
		'Template',
		'template_engine',
		'template_engine_delete'
	);

	/* Dashboard Permissions */
	const CheckEsignDocument = getPermissions(
		'Dashboard',
		'esign',
		'esign_document'
	);
	const CheckDigitalStampOrg = getPermissions(
		'Dashboard',
		'digital_stamp',
		'digital_stamp_org'
	);

	const CheckDigitalInventory = getPermissions(
		'Dashboard',
		'digital_stamp',
		'digital_stamp_inventory_view'
	);
	const isEsignApprover = getPermissions(
		'Approval',
		'dashboard_approval_request',
		'esign_approver'
	);
	const isEsignFlowOn = userInfo?.approvalEsignFlow;
	const isDigitalStampApprover = getPermissions(
		'Approval',
		'dashboard_approval_request',
		'digital_stamp_approver'
	);
	const CheckEsignApprover = isEsignFlowOn && CheckEsignDocument;
	const DashboardApprovalRequest = isEsignApprover || isDigitalStampApprover;
	const ProteanOrderPermission = getPermissions(
		'Dashboard',
		'digital_stamp',
		'digital_stamp_protean'
	);
	const VendorOrderPermission = getPermissions(
		'Dashboard',
		'digital_stamp',
		'digital_stamp_partner'
	);
	const NewEsign = getPermissions('Dashboard', 'esign', 'new_esign');
	const esignDocuments = getPermissions('Dashboard', 'esign', 'esign_document');
	const BulkEsign = getPermissions(
		'Dashboard',
		'bulk_esign',
		'bulk_esign_document'
	);

	/* Wallet Permissions */
	const ProteanWalletTopupView = getPermissions(
		'Wallet',
		'protean_organization_wallet',
		'protean_wallet_topup_view'
	);
	const ProteanWalletTopupAdd = getPermissions(
		'Wallet',
		'protean_organization_wallet',
		'protean_wallet_topup_add'
	);
	const WalletApprover = getPermissions(
		'Approval',
		'wallet_approval_request',
		'wallet_approver'
	);
	const OrgWalletBalanceView = getPermissions(
		'Wallet',
		'protean_organization_wallet',
		'org_wallet_balance_view'
	);
	const OrgWalletTopupAdd = getPermissions(
		'Wallet',
		'protean_organization_wallet',
		'org_wallet_topup_add'
	);
	const WalletEsignConsumption = getPermissions(
		'Wallet',
		'wallet_consumption',
		'wallet_esign_consumption'
	);
	const WalletEtampConsumption = getPermissions(
		'Wallet',
		'wallet_consumption',
		'wallet_etamp_consumption'
	);
	const InvoicesPermission = getPermissions('Wallet', 'invoices', 'invoices');
	const ManageBillingProfilesPermission = getPermissions(
		'Wallet',
		'billing_profile',
		'billing_profile_view'
	);
	const editProfile = getPermissions('Profile', 'profile', 'profile_edit');
	const editUserProfile = getPermissions(
		'Profile',
		'user',
		'user_profile_edit'
	);

	const DigitalStampWalletApprover = getPermissions(
		'Approval',
		'wallet_approval_request',
		'digital_stamp_wallet_approver'
	);

	const viewCancelEsign = getPermissions(
		'Dashboard',
		'esign',
		'new_esign_cancel'
	);

	const viewEditEsignRecipients = getPermissions(
		'Dashboard',
		'esign',
		'new_esign_edit'
	);

	const esignGeneralEdit = getPermissions(
		'Settings',
		'esign_general',
		'esign_general_edit'
	);
	const esignSecurityEdit = getPermissions(
		'Settings',
		'esign_setting',
		'esign_security_edit'
	);
	const lowBalanceEdit = getPermissions(
		'Settings',
		'low_balance',
		'low_balance_notification_edit'
	);

	const viewDigitalStampStats = getPermissions(
		'Dashboard',
		'digital_stamp',
		'digital_stamp_statistics'
	);

	const viewEsignStats = getPermissions(
		'Dashboard',
		'esign',
		'e-sign_statistics'
	);

	const viewBulkEsignDocuments = getPermissions(
		'Dashboard',
		'bulk_esign',
		'bulk_esign_document'
	);

	const uamApproval = getPermissions(
		'Approval',
		'uam_approval_request',
		'user_approver'
	);

	const dashboardApproval = getPermissions(
		'Approval',
		'dashboard_approval_request',
		'esign_approver'
	);

	return {
		HistoryWorkflow,
		HistoryWalletTransaction,
		AccountLedger,
		TemplateEngineView,
		TemplateEngineEdit,
		TemplateEngineAdd,
		TemplateEngineDelete,
		CheckEsignDocument,
		CheckDigitalStampOrg,
		CheckEsignApprover,
		DashboardApprovalRequest,
		ProteanWalletTopupView,
		ProteanWalletTopupAdd,
		WalletApprover,
		WalletEsignConsumption,
		WalletEtampConsumption,
		InvoicesPermission,
		ManageBillingProfilesPermission,
		OrgWalletBalanceView,
		OrgWalletTopupAdd,
		DigitalStampWalletApprover,
		ProteanOrderPermission,
		VendorOrderPermission,
		NewEsign,
		BulkEsign,
		SmsHistory,
		EmailHistory,
		CheckDigitalInventory,
		editProfile,
		editUserProfile,
		viewCancelEsign,
		viewEditEsignRecipients,
		esignGeneralEdit,
		esignSecurityEdit,
		lowBalanceEdit,
		viewDigitalStampStats,
		viewEsignStats,
		viewBulkEsignDocuments,
		esignDocuments,
		uamApproval,
		dashboardApproval
	};
};

export default permissions;
